import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { useTranslate } from "react-admin";

import { color, typography } from "../../theme/designTokens";
import type { DeviceEnrollmentTokenRecord } from "../../types/deviceEnrollment";
import {
  CreatedEnrollmentToken,
  CreateDeviceEnrollmentTokenDialog,
  EnrollmentScopePreset,
} from "./CreateDeviceEnrollmentTokenDialog";
import {
  DeviceEnrollmentTokenDialog,
  DeviceEnrollmentTokenDialogMode,
  EnrollmentTokenSecret,
} from "./DeviceEnrollmentTokenDialog";
import { DeviceEnrollmentTokenList } from "./DeviceEnrollmentTokenList";

type Props = {
  organizationId: string | null;
  preset?: EnrollmentScopePreset;
  createDialogControl?: {
    open: boolean;
    onClose: () => void;
  };
};

type OpenToken = {
  mode: DeviceEnrollmentTokenDialogMode;
  token: DeviceEnrollmentTokenRecord;
  secret: EnrollmentTokenSecret | null;
};

export const DeviceEnrollmentSection = ({ organizationId, preset, createDialogControl }: Props) => {
  const translate = useTranslate();
  const [isInternalCreateOpen, setIsInternalCreateOpen] = useState(false);
  const [openToken, setOpenToken] = useState<OpenToken | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const isCreateOpen = createDialogControl?.open ?? isInternalCreateOpen;
  const onCreateDialogClose = createDialogControl?.onClose;

  const handleCreateClose = useCallback(() => {
    setIsInternalCreateOpen(false);
    onCreateDialogClose?.();
  }, [onCreateDialogClose]);

  const handleCreated = useCallback((created: CreatedEnrollmentToken) => {
    setOpenToken({ mode: "created", token: created.token, secret: created.secret });
    setRefreshKey(current => current + 1);
  }, []);

  const handleShow = useCallback((token: DeviceEnrollmentTokenRecord) => {
    setOpenToken({ mode: "view", token, secret: null });
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }} data-testid="device-enrollment-section">
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mb: 1, gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold }}>
              {translate("deviceEnrollment.section.title")}
            </Typography>
            <Typography variant="body2" color={color.gray}>
              {translate("deviceEnrollment.section.description")}
            </Typography>
          </Box>
          {!createDialogControl && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!organizationId}
              onClick={() => setIsInternalCreateOpen(true)}
              sx={{ backgroundColor: color.primary, color: color.white, whiteSpace: "nowrap" }}
              data-testid="device-enrollment-create-button"
            >
              {translate("deviceEnrollment.section.createButton")}
            </Button>
          )}
        </Box>
        <DeviceEnrollmentTokenList
          key={`${organizationId}:${refreshKey}`}
          organizationId={organizationId}
          refreshKey={refreshKey}
          onShow={handleShow}
        />
      </Box>

      {organizationId && (
        <CreateDeviceEnrollmentTokenDialog
          open={isCreateOpen}
          organizationId={organizationId}
          preset={preset}
          onClose={handleCreateClose}
          onCreated={handleCreated}
        />
      )}

      <DeviceEnrollmentTokenDialog
        open={openToken !== null}
        mode={openToken?.mode ?? "view"}
        token={openToken?.token ?? null}
        secret={openToken?.secret ?? null}
        onClose={() => setOpenToken(null)}
      />
    </Box>
  );
};
